import { AfterViewInit, ContentChildren, ViewChildren, ContentChild, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChild, Component } from "@angular/core";
import { DxDataGridComponent, DxTabsComponent, DxValidationGroupComponent } from "devextreme-angular";
import { Observable, Subscription } from "rxjs";
import { SectionComponent } from "@ui/layouts/fieldlayouts/section/section.component";
import { FrameworkService } from "@core/framework/framework.service";
import { EnumCRUDActions, SysEnumsService } from "@core/framework/sysenums.enum";
import { SysGlobals } from "@core/framework/sysglobals";
import { Utils } from "@core/helpers/Utils";
import { BaseDataSourceAbstract } from "../datalayer/BaseDataSourceAbstract";
import { BaseEntity, BaseResponse, BaseState, CompanyInfo, GenericListEntity } from "../entities/BaseEntities";
import { EntityLoadedEventArgs, EntitySavedEventArgs } from "../classes/BaseClasses";
import { ServiceBagService } from "./ServiceBag";
import { EnumSystemOptions } from "@core/framework/systemoptions.enum";
import { IBaseComponent, IBaseModuleComponent } from './IBaseComponent';
import { dxValidationGroupResult } from "devextreme/ui/validation_group";
import { UiService } from '@core/framework/ui.service';
import { BottomSheetContext } from 'swipe-bottom-sheet/angular';

@Component({
    selector: 'base-component',
    template: '<div></div>'
})
/** Base component with state */
export abstract class BaseComponent<TState extends BaseState> implements OnInit, AfterViewInit, OnDestroy, OnChanges, IBaseComponent, IBaseModuleComponent {
    //Class members
    public State: TState = {} as TState;
    public DataSources: BaseDataSourceAbstract[] = {} as BaseDataSourceAbstract[];
    //public Z = this.A.AppConfigService.AppConfig.SystemOptions;

    public ModuleName: string = this.A.AppConfigService.getModuleNameByInstance(this);
    public BottomSheetContext: BottomSheetContext<any>;

    private subscribtionOnEntitySaved: Subscription;
    private subscribtionOnEntityLoaded: Subscription;
    private subscribtionOnReloadComponent: Subscription;

    //Child component iterators and combiners.
    //Since content may be injected from outside, consider both options here
    @ViewChild('FocusOnMe') private FocusOnMe_V: any;
    @ContentChild('FocusOnMe') private FocusOnMe_C: any;
    private get FocusOnMe(): any { return Utils.CombineChild(this.FocusOnMe_V, this.FocusOnMe_C); }
    @ViewChildren(SectionComponent) private Sections_V: QueryList<SectionComponent>;
    @ContentChildren(SectionComponent) private Sections_C: QueryList<SectionComponent>;
    private get Sections(): SectionComponent[] { return Utils.CombineChildren(this.Sections_V, this.Sections_C) }
    @ViewChildren(DxTabsComponent) Tabs: QueryList<DxTabsComponent>;
    @ViewChildren(DxDataGridComponent) Grids: QueryList<DxDataGridComponent>;

    //Overrides - for usage by actual components
    protected OnInitComponent() { }
    protected OnInitComponentFinished() { }
    protected OnStartInitCycle() { }
    protected OnDestroyComponent() { }
    protected OnInitDataSources() { }
    protected OnInitState() { }
    protected OnExternalEntitySaved(ExternalEntity: BaseEntity, ExternalEntityType: new () => BaseEntity, ExternalEntityAction: EnumCRUDActions) { }
    protected OnExternalEntityLoaded(ExternalEntity: BaseEntity, ExternalEntityType: new () => BaseEntity) { }
    protected OnInputPropertiesChanged(changes: SimpleChanges) { }
    protected OnGetCustomComponentStateName(): string { return undefined; }
    protected IsAutoFocusEnabled(): boolean { return true; }
    protected IsUseFinishCycleChangeDetection(): boolean { return true; }

    //Internal overrides - for usage by BaseComponent* framework classes only
    protected OnInitComponentInternal() { }
    protected OnInitComponentFinishedInternal() { }
    protected OnInitComponentFinishedAfterDetectInternal() { }
    protected OnStartInitCycleInternal() { }
    protected OnDestroyComponentInternal() { }

    constructor
        (
            public A: ServiceBagService,
            public ComponentName: string,
            protected StateType: (new () => TState)
        ) {
        this.State = Utils.GetNewInstance<TState>(this.StateType);
    }


    //Return name used for state, configuration, etc
    public get ComponentStateName(): string {
        var sCustom: string = this.OnGetCustomComponentStateName();
        return sCustom != undefined ? sCustom : this.ComponentName;
    }

    protected InitState() {
        //Init state
        this.State = {...Utils.GetNewInstance<TState>(this.StateType), ...this.State};

        //Attempt to load saved state
        var bStateLoaded: boolean;
        if (!SysGlobals.IsResetState) bStateLoaded = this.LoadState();
        SysGlobals.IsResetState = false;

        //If no saved state was loaded, init fresh
        if (!bStateLoaded) this.OnInitState();

        //Set
        SysGlobals.CurrentState = this.State;
    }

    //Run internal change detection, in cases when Angular does not detect on its own. For instance, when changing references inside State
    protected DetectChanges() {
        if (this.A.ChangeDetector == undefined) return;
        if ((this.A.ChangeDetector as any).destroyed) return;
        this.A.ChangeDetector.detectChanges();
    }

    public get UI(): UiService {
        return this.A.UI;
    }

    public get UserInfo(): any {
        return this.ModuleName && this.A.Session.UserInfoDict[this.ModuleName];
    }

    //This construction is required so that the component can access this member
    public DS(Name: string): BaseDataSourceAbstract {
        return this.DataSources[Name] ? this.DataSources[Name] : this.A.Session.DataSources ? this.A.Session.DataSources[`${this.ModuleName}${Name}`] : null;
    }

    public get Enums(): any {
        return this.A.Enums;
    }

    public get SysEnums(): SysEnumsService {
        return this.A.SysEnums;
    }

    public LIST(sListName): GenericListEntity[] {
        return this.A.ResourcesService.LIST(sListName, this.ModuleName);
    }

    public LISTITEM(sListName, sValue): string {
        return this.A.ResourcesService.LISTITEM(sListName, sValue, this.ModuleName);
    }

    public LISTITEMX(sListName, sValue): GenericListEntity {
        return this.A.ResourcesService.LISTITEMX(sListName, sValue, this.ModuleName);
    }

    public RC(value, ...args: string[] | number[]): string {
        return this.A.ResourcesService.RC(value, this.ModuleName, ...args);
    }

    public RCF(value, ...args: string[] | number[]): string {
        return this.A.ResourcesService.RC(value, '', ...args);
    }

    public get Framework(): FrameworkService {
        return this.A.Framework;
    }

    public CustomBuild(build: string, execute: () => void) {
        if (this.Framework.IsCustomBuild(build)) execute();
    }

    public IsCustomBuild(build: string): boolean {
        return this.Framework.IsCustomBuild(build);
    }

    //Preserve state into global variable
    SaveState() {
        SysGlobals.PageStates[this.ComponentStateName] = this.State;
    }

    //Load state from global variable
    protected LoadState(): boolean {
        var oState = SysGlobals.PageStates[this.ComponentStateName];
        if (oState == undefined) return false;
        this.State = Utils.CloneObject(oState);
        return true;
    }


    //Go to route
    public Navigate(params: any[], optionalParams?: any, bSaveState: boolean = false, bSaveCurrentLocation = false) {
        //Preserve current state
        if (this.IsNeedSaveState(params)) this.SaveState();
        return this.A.Navigator.Navigate(params, optionalParams);
    }

    private IsNeedSaveState(params: any[]) {
        const path = params[0] as string;
        return !path.includes('system');
    }

    //Go back
    public NavigateBack() {
        this.A.RouteHistoryService.GoBack();
        return Promise.resolve(true);
    }

    ngOnChanges(changes: SimpleChanges) {
        this.OnInputPropertiesChanged(changes);
    }

    //Attach events
    private SubscribeEvents() {
        this.subscribtionOnEntitySaved = this.A.SysEvents.OnEntitySaved.subscribe((e: EntitySavedEventArgs) => this.OnExternalEntitySaved(e.Entity, e.Type, e.Action));
        this.subscribtionOnEntityLoaded = this.A.SysEvents.OnEntityLoaded.subscribe((e: EntityLoadedEventArgs) => this.OnExternalEntityLoaded(e.Entity, e.Type));
        this.subscribtionOnReloadComponent = this.A.SysEvents.OnReloadComponent.subscribe((sComponentName: string) => this.OnReloadComponent(sComponentName));
    }

    //Detach events
    private UnsubscribeEvents() {
        this.subscribtionOnEntitySaved && this.subscribtionOnEntitySaved.unsubscribe();
        this.subscribtionOnEntityLoaded && this.subscribtionOnEntityLoaded.unsubscribe();
        this.subscribtionOnReloadComponent && this.subscribtionOnReloadComponent.unsubscribe();
    }

    //Reload event handler
    protected OnReloadComponent(sComponentName: string) {
        if (this.ComponentName == sComponentName) this.ReloadComponent();
    }

    //Fully reload the component, as if user just navigated to it
    //Use SetTimeout in order for the navigation changes to be applied
    protected ReloadComponent(sComponentName: string = undefined) {
        if (sComponentName == undefined || sComponentName == this.ComponentName) {
            //Reload the current component
            setTimeout(() => {
                this.RunDestroyCycle(false);
                this.RunInitStartCycle(false);
                this.RunInitFinishCycle(false);
            }, 100);
        }
        else {
            //Send a message to reload a different one
            this.A.Navigator.ReloadComponent(sComponentName);
        }
    }

    //Lifecycle event executors
    protected RunInitStartCycle(bFromAngularEvent: boolean) { this.RunInitStartCycleInternal(); }
    protected RunInitFinishCycle(bFromAngularEvent: boolean) { this.RunInitFinishCycleInternal(); }
    protected RunDestroyCycle(bFromAngularEvent: boolean) { this.RunDestroyCycleInternal(); }

    //Lifecycle event hooks
    ngOnInit() { this.RunInitStartCycle(true); }
    ngAfterViewInit() { this.RunInitFinishCycle(true); }
    ngOnDestroy() { this.RunDestroyCycle(true); }

    //Component initialization start cylce
    private RunInitStartCycleInternal() {
        //Before life-cycle events for child component overrides
        this.OnStartInitCycleInternal();
        this.OnStartInitCycle();

        //Page initialization lifecycle
        this.SubscribeEvents();
        this.InitState();
        this.OnInitDataSources();
        this.OnInitComponentInternal();
        this.OnInitComponent();
    }

    //Component initialization finish cylce
    private RunInitFinishCycleInternal() {
        //Focus on first textbox
        this.InitFocus();

        //Allow override
        this.OnInitComponentFinishedInternal();
        this.OnInitComponentFinished();

        //Run detection since FieldCustomizations set some values on the controls after view has been checked
        //!!! Code below -> PERFORMANCE GAP.
        //if (SysGlobals.ComponentCustomizations?.length || SysGlobals.FieldCustomizations?.length) {
        if (this.IsUseFinishCycleChangeDetection()) { this.DetectChanges(); }

        //Allow override after detect
        this.OnInitComponentFinishedAfterDetectInternal();

        this.CustomizeComponentsVisibility();
    }

    CustomizeComponentsVisibility() {
        if (!SysGlobals.ComponentCustomizations) return;

        //Customize components if already on the page
        this.A.CustomizationService.SetComponentCustomizations(this.Grids.toArray(), this.ComponentStateName);
        this.A.CustomizationService.SetComponentCustomizations(this.Sections_V.toArray(), this.ComponentStateName);
        const tabsItems = this.Tabs.toArray().reduce((p, c) => [...p, ...c.items], []);
        this.A.CustomizationService.SetComponentCustomizations(tabsItems, this.ComponentStateName);

        //Customize components after they will appear on the page by some conditions
        this.Grids.changes.subscribe((grids) => {
            this.A.CustomizationService.SetComponentCustomizations(grids.toArray(), this.ComponentStateName);
        });
        this.Sections_V.changes.subscribe((sections) => {
            this.A.CustomizationService.SetComponentCustomizations(sections.toArray(), this.ComponentStateName);
        });
        this.Tabs.changes.subscribe((tabs) => {
            const tabsItems = tabs.toArray().reduce((p, c) => [...p, ...c.items], []);
            this.A.CustomizationService.SetComponentCustomizations(tabsItems, this.ComponentStateName);
        });
    }

    //Destruction cycle
    private RunDestroyCycleInternal() {
        this.UnsubscribeEvents();

        //Overrides
        this.OnDestroyComponentInternal();
        this.OnDestroyComponent();
    }

    //Focus on the marked control
    public InitFocus() {
        if (this.FocusOnMe && this.FocusOnMe.instance && this.IsAutoFocusEnabled()) setTimeout(() => this.FocusOnMe.instance.focus(), 500);
    }

    //Generic executer for observable
    protected Do<T extends BaseResponse>(serviceResponse: Observable<T | Blob>, bShowSuccessMessage: boolean = true, after?: (response: T) => void, fail?: (response: T) => void) {
        this.Framework.Do(serviceResponse, bShowSuccessMessage, after, fail);
    }

    //Lock controls. Use setTimeout since it's usually called after all kinds of values are changed, to allow DetectChanges to run before
    public LockControls(bLock: boolean) {
        setTimeout(() => {
            this.Sections.forEach(x => x.LockControls(bLock))
        });
    }

    //Reset the validation.
    protected ResetValidation() {
        this.Sections.forEach(x => x.ResetValidation());
    }

    //todo: rename it 
    protected AcceptGridChanges(): Promise<boolean>[] {
        const gridValidations = new Array<Promise<boolean>>();
        this.Sections.forEach(x => x.AcceptGridChanges().forEach(v => gridValidations.push(v)));
        return gridValidations;
    }

    public SystemOptionFlag(OptionName: EnumSystemOptions | string): boolean {
        return this.Framework.SystemOptionFlag(OptionName);
    }

    public SystemOption(OptionName: EnumSystemOptions | string): any {
        return this.Framework.SystemOption(OptionName);
    }

    //Raise an event to notify potential subscribers that the entity was saved
    //This is in the base class since different components might be using it, not only entity ones
    protected NotifyEntitySaved(Entity: BaseEntity, Type: new () => BaseEntity, Action: EnumCRUDActions = EnumCRUDActions.CreateOrUpdate) {
        const args = new EntitySavedEventArgs(Entity, Type, Action);
        this.A.EntityService.SetLastSavedEntity(args);
        this.A.SysEvents.NotifyEntitySaved(args);
    }

    protected NotifyEntityLoaded(Entity: BaseEntity, Type: new () => BaseEntity) {
        this.A.SysEvents.OnEntityLoaded.emit(new EntityLoadedEventArgs(Entity, Type));
    }

    public get THIS(): BaseComponent<TState> {
        return this;
    }

    //Validate the group, and focus on the first elemt
    public ValidateValidationGroup(validationGroup: DxValidationGroupComponent, beforeMoveFocus?: () => void): Promise<boolean> {
        //Validate
        var result = validationGroup.instance.validate();
        if (result.status === "pending") return new Promise<boolean>((resolve) => {
            (<Promise<dxValidationGroupResult>>result.complete).then(res => resolve(res.isValid))
        });

        return new Promise<boolean>((resolve) => {
            if (result.isValid) {
                resolve(true);
                return;
            }

            if (beforeMoveFocus) beforeMoveFocus();

            //Focus and return error. Use SetTimeout since beforeMoveFocus may take some time to reflect
            setTimeout(() => {
                (result.brokenRules[0] as any).validator.focus();
                //Call validate method after focus again to redraw controls for correct validation error message position
                setTimeout(() => validationGroup.instance.validate(), 0);
            }, 100);
            resolve(false);
        });
    }

    //Shortcut to specify formats
    public get F(): CompanyInfo {
        return this.A.Framework.CompanyInfo;
    }

    //Return whether user has one of the permissions
    public IsAllow(permissions: string[]) {
        if (!permissions || permissions.length == 0) return true;
        return permissions.some((p) => this.UserInfo && this.UserInfo.hasOwnProperty('IsAllow' + p) && this.UserInfo['IsAllow' + p]);
    }

    //Filter out events that are not generated by UI
    //Include special way to force event by passing e==true
    protected IsUIEvent(e: any): boolean {
        if (e == true) return true;
        return (e != undefined && e.event != undefined);
    }

    //Bind context this to the event. It cannot be done other way
    protected BindHandler(sHandlerName: string) {
        if (this[sHandlerName] == undefined) throw (`Invalid handler ${sHandlerName} was specified`);
        this[sHandlerName] = this[sHandlerName].bind(this);
    }


}
